import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, Loader2, ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SavedReports = ({ onLogout }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (report) => {
    if (report.report_type === 'australian_emerging_leaders') {
      navigate(`/report/${report.id}`);
    } else if (report.report_type === 'nitro') {
      navigate(`/nitro-report/${report.id}`);
    } else if (report.report_type === 'fresh-clinics-valuation-v1' || report.report_type === 'fresh_clinics_v1') {
      navigate(`/fresh-clinics-report-v1/${report.id}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Sydney'
    });
  };

  const getReportTypeName = (type) => {
    const typeMap = {
      'australian_emerging_leaders': 'Australian Emerging Leaders Fund',
      'nitro': 'JAADE - Valuation Report - Nitro',
      'nitro': 'JAADE - Valuation Report - Nitro',
      'fresh-clinics-valuation-v1': 'JAADE - Valuation Report - Fresh Clinics (v1)',
      'fresh_clinics_v1': 'JAADE - Valuation Report - Fresh Clinics (v1)',
    };
    return typeMap[type] || type;
  };

  const filteredReports = filter === 'all'
    ? reports
    : reports.filter(r => {
        if (filter === 'nitro') {
          return r.report_type === 'nitro';
        }
        if (filter === 'fresh_clinics_v1') {
          return r.report_type === 'fresh-clinics-valuation-v1' || r.report_type === 'fresh_clinics_v1';
        }
        return r.report_type === filter;
      });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Generator
              </Button>
              <h1 className="text-2xl font-bold text-slate-800">Saved Reports</h1>
            </div>
            <Button variant="outline" onClick={onLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All Reports ({reports.length})
          </Button>
          <Button
            variant={filter === 'australian_emerging_leaders' ? 'default' : 'outline'}
            onClick={() => setFilter('australian_emerging_leaders')}
          >
            Australian Emerging Leaders ({reports.filter(r => r.report_type === 'australian_emerging_leaders').length})
          </Button>
          <Button
            variant={filter === 'nitro' ? 'default' : 'outline'}
            onClick={() => setFilter('nitro')}
          >
            Nitro ({reports.filter(r => r.report_type === 'nitro').length})
          </Button>
          <Button
            variant={filter === 'fresh_clinics_v1' ? 'default' : 'outline'}
            onClick={() => setFilter('fresh_clinics_v1')}
          >
            Fresh Clinics v1 ({reports.filter(r => r.report_type === 'fresh-clinics-valuation-v1' || r.report_type === 'fresh_clinics_v1').length})
          </Button>
        </div>

        {/* Reports Grid */}
        {filteredReports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">No reports found</p>
              <Button className="mt-4" onClick={() => navigate('/')}>
                Generate Your First Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReports.map((report) => (
              <Card
                key={report.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleViewReport(report)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <FileText className="w-8 h-8 text-slate-400" />
                    <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                      {getReportTypeName(report.report_type)}
                    </span>
                  </div>
                  <CardTitle className="text-lg mt-4 line-clamp-2">
                    {report.title}
                  </CardTitle>
                  {report.subtitle && (
                    <p className="text-sm text-slate-600">{report.subtitle}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm text-slate-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{formatDate(report.created_at)}</span>
                  </div>
                  {report.quarter && report.year && (
                    <div className="mt-2 text-xs text-slate-600">
                      Q{report.quarter} {report.year}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedReports;
